import { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import FullDocumentationView from "../app/project/FullDocumentationView"
import { adminApi } from "../../services/api"

export default function AdminDocumentationDetails () {
  const { id } = useParams()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [doc, setDoc] = useState(null)
  const [downloading, setDownloading] = useState("")

  useEffect(() => {
    let mounted = true
    setLoading(true)
    setError("")
    setDoc(null)
    adminApi.documentations.get(id)
      .then((res) => {
        if (!mounted) return
        setDoc(res?.data?.data || null)
      })
      .catch((e) => {
        if (!mounted) return
        setError(e?.response?.data?.message || e.message || "Failed to load documentation")
      })
      .finally(() => {
        if (!mounted) return
        setLoading(false)
      })
    return () => { mounted = false }
  }, [id])

  const fileBaseName = useMemo(() => {
    const title = doc?.project?.title || "documentation"
    return String(title)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || "documentation"
  }, [doc?.project?.title])

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  }

  const exportPdf = async () => {
    if (!id) return
    setDownloading("pdf")
    setError("")
    try {
      const res = await adminApi.documentations.exportPdf(id)
      const blob = new Blob([res.data], { type: "application/pdf" })
      downloadBlob(blob, `${fileBaseName}.pdf`)
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to export PDF")
    } finally {
      setDownloading("")
    }
  }

  const exportDocx = async () => {
    if (!id) return
    setDownloading("docx")
    setError("")
    try {
      const res = await adminApi.documentations.exportDocx(id)
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      })
      downloadBlob(blob, `${fileBaseName}.docx`)
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to export Word")
    } finally {
      setDownloading("")
    }
  }

  return (
    <div className="min-h-screen bg-base-200 text-base-content">
      <div className="p-4">
        <div className="mx-auto max-w-6xl">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold">Documentation</h1>
              <p className="text-sm text-base-content/60">
                {doc?.student?.email ? `Student: ${doc.student.email}` : "Admin view"}
              </p>
              <p className="text-xs text-base-content/60 mt-1">
                Type: {doc?.generationType === "PROPOSAL" ? "Proposal" : doc?.generationType === "SIWES_REPORT" ? "SIWES Report" : "Documentation"}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button className="btn btn-sm" type="button" onClick={() => navigate("/admin/dashboard?tab=documentation")}>
                Back
              </button>
              <button className="btn btn-sm btn-outline" type="button" onClick={exportPdf} disabled={!doc || downloading}>
                {downloading === "pdf" ? "Exporting PDF..." : "Export PDF"}
              </button>
              <button className="btn btn-sm btn-outline" type="button" onClick={exportDocx} disabled={!doc || downloading}>
                {downloading === "docx" ? "Exporting Word..." : "Export Word"}
              </button>
            </div>
          </div>

          {error ? <div className="alert alert-error mb-4"><span>{error}</span></div> : null}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <span className="loading loading-spinner loading-lg text-primary"></span>
            </div>
          ) : (
            <div className="rounded-2xl border border-base-300 bg-base-100 p-3 sm:p-5">
              <FullDocumentationView
                doc={doc}
                setPage={() => {}}
                setSelectedDoc={() => {}}
                setSelectedChapter={() => {}}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

